import React, { useState, useEffect } from 'react';
import clientDB from '../config/clientDatabase';
import './PowerBIManager.css';

const PowerBIManager = ({ clientId, clientName, onClose }) => {
  const [reports, setReports] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [newReport, setNewReport] = useState({
    title: '',
    embedUrl: '',
    description: '',
    category: 'Dashboard'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReports();
  }, [clientId]);

  const loadReports = () => {
    const clientReports = clientDB.getCompanyPowerBIReports(clientId);
    setReports(clientReports);
  };

  const handleAddReport = async () => {
    if (!newReport.title || !newReport.embedUrl) {
      alert('Please fill in at least the title and embed URL');
      return;
    }

    setLoading(true);
    try {
      await clientDB.addPowerBIReport(clientId, newReport);
      loadReports();
      setShowAddModal(false);
      setNewReport({
        title: '',
        embedUrl: '',
        description: '',
        category: 'Dashboard'
      });
      alert('Power BI report added successfully!');
    } catch (error) {
      console.error('Error adding report:', error);
      alert('Failed to add report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReport = async () => {
    if (!editingReport.title || !editingReport.embedUrl) {
      alert('Please fill in at least the title and embed URL');
      return;
    }

    setLoading(true);
    try {
      await clientDB.updatePowerBIReport(clientId, editingReport.id, editingReport);
      loadReports();
      setEditingReport(null);
      alert('Power BI report updated successfully!');
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Failed to update report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this Power BI report?')) {
      return;
    }

    setLoading(true);
    try {
      await clientDB.removePowerBIReport(clientId, reportId);
      loadReports();
      alert('Power BI report deleted successfully!');
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const testEmbedUrl = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="powerbi-manager-overlay">
      <div className="powerbi-manager-container">
        <div className="powerbi-manager-header">
          <h2>📊 Power BI Reports - {clientName}</h2>
          <div className="header-actions">
            <button
              onClick={() => setShowAddModal(true)}
              className="add-report-button"
            >
              + Add Report
            </button>
            <button onClick={onClose} className="close-button">✕</button>
          </div>
        </div>

        <div className="powerbi-manager-content">
          {reports.length === 0 ? (
            <div className="no-reports">
              <div className="no-reports-icon">📈</div>
              <h3>No Power BI Reports</h3>
              <p>Add Power BI reports for {clientName} to access in their portal</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="add-first-report-button"
              >
                Add First Report
              </button>
            </div>
          ) : (
            <div className="reports-grid">
              {reports.map(report => (
                <div key={report.id} className="report-card">
                  <div className="report-header">
                    <div className="report-icon">📊</div>
                    <div className="report-info">
                      <h4>{report.title}</h4>
                      <span className="report-category">{report.category}</span>
                    </div>
                  </div>

                  <div className="report-description">
                    {report.description || 'No description provided'}
                  </div>

                  <div className="report-url">
                    <small>Embed URL:</small>
                    <div className="url-preview">
                      {report.embedUrl.substring(0, 60)}...
                    </div>
                  </div>

                  <div className="report-actions">
                    <button
                      onClick={() => testEmbedUrl(report.embedUrl)}
                      className="test-button"
                      title="Test in new tab"
                    >
                      🔗 Test
                    </button>
                    <button
                      onClick={() => setEditingReport(report)}
                      className="edit-button"
                      title="Edit report"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="delete-button"
                      title="Delete report"
                    >
                      🗑️ Delete
                    </button>
                  </div>

                  <div className="report-meta">
                    <small>Added: {new Date(report.addedDate).toLocaleDateString()}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Report Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <div className="modal-header">
                <h3>Add Power BI Report</h3>
                <button onClick={() => setShowAddModal(false)} className="modal-close">✕</button>
              </div>

              <div className="modal-content">
                <div className="form-group">
                  <label>Report Title *</label>
                  <input
                    type="text"
                    value={newReport.title}
                    onChange={(e) => setNewReport({...newReport, title: e.target.value})}
                    placeholder="e.g., Monthly Dashboard"
                  />
                </div>

                <div className="form-group">
                  <label>Power BI Embed URL *</label>
                  <textarea
                    value={newReport.embedUrl}
                    onChange={(e) => setNewReport({...newReport, embedUrl: e.target.value})}
                    placeholder="https://app.powerbi.com/reportEmbed?reportId=..."
                    rows="3"
                  />
                  <small>Get this from Power BI → File → Embed → Website or portal</small>
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newReport.category}
                    onChange={(e) => setNewReport({...newReport, category: e.target.value})}
                  >
                    <option value="Dashboard">Dashboard</option>
                    <option value="Financial">Financial</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Operations">Operations</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newReport.description}
                    onChange={(e) => setNewReport({...newReport, description: e.target.value})}
                    placeholder="Brief description of what this report shows..."
                    rows="2"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button onClick={() => setShowAddModal(false)} className="cancel-button">
                  Cancel
                </button>
                <button
                  onClick={handleAddReport}
                  disabled={loading}
                  className="save-button"
                >
                  {loading ? 'Adding...' : 'Add Report'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Report Modal */}
        {editingReport && (
          <div className="modal-overlay">
            <div className="modal-container">
              <div className="modal-header">
                <h3>Edit Power BI Report</h3>
                <button onClick={() => setEditingReport(null)} className="modal-close">✕</button>
              </div>

              <div className="modal-content">
                <div className="form-group">
                  <label>Report Title *</label>
                  <input
                    type="text"
                    value={editingReport.title}
                    onChange={(e) => setEditingReport({...editingReport, title: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Power BI Embed URL *</label>
                  <textarea
                    value={editingReport.embedUrl}
                    onChange={(e) => setEditingReport({...editingReport, embedUrl: e.target.value})}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editingReport.category}
                    onChange={(e) => setEditingReport({...editingReport, category: e.target.value})}
                  >
                    <option value="Dashboard">Dashboard</option>
                    <option value="Financial">Financial</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Operations">Operations</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editingReport.description}
                    onChange={(e) => setEditingReport({...editingReport, description: e.target.value})}
                    rows="2"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button onClick={() => setEditingReport(null)} className="cancel-button">
                  Cancel
                </button>
                <button
                  onClick={handleUpdateReport}
                  disabled={loading}
                  className="save-button"
                >
                  {loading ? 'Updating...' : 'Update Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerBIManager;